<script>
import ItemService from '@/services/ItemService.js'
import FullImg from '@/components/FullImg'
import '@/components/BlurOnLeave'

export default {
	name: 'ItemsView',
	components: { FullImg },
	props: [
		'id'
	],
	data () {
		return {
			item: null,
			selectedIndex: 0,
			slideTransition: ''
		}
	},
	watch: {
		id: {
			async handler (id) {
				// slide transition direction
				if (this.item)
					this.slideTransition = id === this.item.previous.id ? 'slide-right' : 'slide-left';
				// new item
				this.item = await ItemService.getItem(id);
				// reset picture index to avoid keeping an index larger than new item picture array
				this.selectedIndex = 0;
			},
			immediate: true
		}
	},
	methods: {
		prev () {
			this.$router.push(this.item.previous.id);
		},
		next () {
			this.$router.push(this.item.next.id);
		}
	}

}

</script>
<template>

<div class="item-view" v-if="item">

	<router-link class="nav back ripple" to="/" title="Retour à la liste">
		<span>⊞⤶</span>
	</router-link>

	<a class="nav prev ripple" href="#" @click.prevent="prev" v-blur-on-leave>
		<span>〈</span>
	</a>

	<a class="nav next ripple" href="#" @click.prevent="next" v-blur-on-leave>
		<span>〉</span>
	</a>

	<transition :name="slideTransition">
	<div class="main" :key="item.id" :data-flip-key="'item-' + item.id">
		<div class="header">
			<div class="label">
				{{ item.label }}
			</div>
			<div class="info">
				<div class="descr" v-if="item.descr">{{ item.descr }}</div>
				<div class="descr" v-if="item.material">{{ item.material }}</div>
			</div>
		</div>
		<div class="content" v-touch:swipe.left="next" v-touch:swipe.right="prev">
			<img class="picture" :src="item.pictures[selectedIndex]" />
		</div>
		<div class="thumbnails" v-if="item.pictures.length > 1">
			<a class="thumbnail bubble ripple"
				href="#"
				v-for="(picture, index) in item.pictures"
				:key="index"
				:class="{ active: selectedIndex == index }"
				@click.prevent="selectedIndex = index"
				v-blur-on-leave>
				<FullImg :src="picture" />
			</a>
		</div>
	</div>
	</transition>

</div>

</template>
<style scoped>

/*** Layout ***/

/* Full screen view */
.item-view {
	position: fixed;
}
.main {
	position: absolute;
}
.item-view , .main {
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	overflow: hidden;
}

/* View layout */
.main {
	display: flex;
	flex-flow: column nowrap;
}
.content {
	flex: 1;
}

/* Header layout */
.header {
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	padding: .6em .8em;
}
.label {
	padding-right: .6em;
}
.info {
	flex: 1;
}
@media screen and (max-width: 424px) {
	.info {
		flex-basis: 100%;
	}
	.descr {
		display: inline;
	}
	.descr:not(:first-child)::before {
		content: ' - ';
	}
}
@media screen and (min-width: 425px) {
	.info {
		margin-top: .5em;
	}
}
@media screen and (min-width: 1280px) {
	.label {
		margin-left: 10%;
	}
}

/* Content layout */
.content {
	position: relative;
}
.picture {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	max-width: 100%;
	max-height: 100%;
}
@media screen and (min-width: 1280px) {
	.picture {
		max-height: 95%;
		border-radius: 5px;
	}
}

/* Thumbnails layout */
.thumbnails {
	display: flex;
	flex-flow: row wrap-reverse;
	align-items: center;
	justify-content: flex-end;
	height: 4em;
	margin-right: .4em;
}
.thumbnail {
	margin: .4em;
}
.thumbnail .full-img {
	width: 2.8em;
	height: 2.8em;
}
.thumbnail, .thumbnail .full-img {
	border-radius: 100%;
}

/* Thumbnails placement */
.thumbnails {
	position: absolute;
	z-index: 1;
}
@media screen and (max-width: 767px) {
	.main {
		padding-bottom: 4em;
	}
	.thumbnails {
		bottom: 0;
		right: 0;
		left: 4em;
	}
}
@media screen and (min-width: 768px) {
	.thumbnails {
		top: 0;
		right: 0;
	}
}
@media screen and (min-width: 1280px) {
	.thumbnails {
		right: 10%;
	}
}

/* Nav layout */
.nav span {
	font-size: 2.2em;
	line-height: .8em;
}

/* Nav placement */
.nav {
	position: absolute;
	z-index: 1;
}
.prev {
	left: 0;
}
.next {
	right: 0;
}
.back {
	left: 0;
	bottom: 0;
}
@media screen and (max-width: 767px) {
	.header {
		margin: 0 2em;
	}
	.prev, .next {
		top: 0;
		padding: .8em;
	}
	.back {
		padding: 1.2em .8em;
	}
}
@media screen and (max-width: 424px) {
	.prev, .next {
		padding: 1.4em .8em;
	}
}
@media screen and (min-width: 768px) {
	.prev, .next {
		top: 55%;
		transform: translateY(-50%);
		padding: 2em;
	}
	.back {
		padding: 1em;
	}
}
@media screen and (min-width: 1280px) {
	.prev, .next {
		padding: 3em;
	}
	.back {
		padding: 1.5em;
	}
}

/*** Theme ***/

/* Header */
.label {
	font: var(--app-font-title);
	font-size: 275%;
	line-height: 1em;
	color: #FFF;
}
.descr {
	font: var(--app-font-text);
	line-height: 1em;
	color: #AAA;
}

/* Thumbnails */
.thumbnails {
	--bubble-scale: 1.5;
	--bubble-color: #ff7f00;
	--bubble-opacity-active: .5;
}
.thumbnail {
	border: solid 2px rgba(255,255,255,.1);
}
.thumbnail.active {
	border-color: #ff7f00;
}

/* Content */
@media screen and (min-width: 425px) {
	.content {
		border-top: solid 1px #777;
		border-bottom: solid 1px #444;
	}
}

/* Nav */
.nav {
	color: #EEE;
}
.nav:hover, .nav:active, .nav:focus {
	color: #ff7f00;
}

/*** Transitions ***/

.slide-left-enter-active, .slide-left-leave-active,
.slide-right-enter-active, .slide-right-leave-active {
	transition: all .3s;
}
.slide-left-enter, .slide-left-leave-to,
.slide-right-enter, .slide-right-leave-to {
	opacity: 0;
}
.slide-left-leave-active,
.slide-right-leave-active {
	position: absolute;
	width: 100%;
}

.slide-left-enter, .slide-right-leave-to {
	transform: translateX(120%);
}
.slide-left-leave-to, .slide-right-enter {
	transform: translateX(-120%);
}

</style>
